import {Box, Flex} from '@indoqa/style-system'
import {IStyle} from 'fela'
import * as React from 'react'
import {useFela} from 'react-fela'
import {useHistory} from 'react-router-dom'
import {centeredLayoutRules, clippedBackgroundImageRules, containerRules} from '../../../app/mixins'
import {Theme} from '../../../app/theme'
import {ThemedIcon} from '../../../commons-shared/themed-icon/ThemedIcon'
import {HeaderPanel} from '../../../commons/components/header/HeaderPanel'
import {Breadcrumb, ReaderBreadcrumbsPanel} from '../../../commons/components/header/ReaderBreadcrumbsPanel'
import {useMobile} from '../../../commons/hooks/use-mobile/useMobile'
import {ReactComponent as CloseIcon} from '../../../commons/icons/close.svg'
import {ReactComponent as TocIcon} from '../../../commons/icons/table-of-content.svg'
import {ReaderLayoutContext, ReaderLayoutContextValue} from '../../../commons/layouts/reader-layout/ReaderLayoutContext'
import {DocumentInfo} from '../../../commons/types/DocumentInfo'
import {DigitalLogo} from '../../../digital/components/digital-logo/DigitalLogo'
import {useKiara} from '../../components/kiara/KiaraContext'
import kiaraLogo from '../../components/kiara/logo-kiara.svg'
import arSearchAreaBackgroundImage from '../../components/search/ArOverviewSearchBg.jpg'
import {ArFilters} from '../../store/ar.actions'
import {arPathBook, arPathContentResources, arPathSearch} from '../../store/ar.paths'
import {ArSearchInput} from '../search/ArSearchInput'

const KIARA_LOGO_SIZE = 20
const AR_SEARCH_AREA_HEIGHT = 79

interface Props {
  noSearch?: boolean
  query?: string | null
  filters?: ArFilters
  page?: number
  bookName?: string
  bookId?: string | null
  documentInfo?: DocumentInfo | null
  paraId?: string | null
}

export const ARHeaderPanel: React.FC<Props> = ({
  noSearch,
  query,
  filters,
  page,
  bookName,
  bookId,
  documentInfo,
  paraId,
}) => {
  const {css, theme} = useFela<Theme>()
  const {kiaraEnabled, showKiara, toggleKiara, closeKiara} = useKiara()
  const history = useHistory()
  const mobile = useMobile()
  const {setHeaderHeight, navShown, toggleNav} = React.useContext<ReaderLayoutContextValue>(ReaderLayoutContext)
  const initial = React.useRef(true)

  React.useLayoutEffect(() => {
    const barsHeight = theme.layout.header.height + theme.layout.reader.toolbarHeight
    setHeaderHeight(noSearch ? barsHeight : barsHeight + AR_SEARCH_AREA_HEIGHT)
  }, [setHeaderHeight, noSearch, theme.layout.header.height, theme.layout.reader.toolbarHeight])

  React.useEffect(() => {
    if (initial && mobile && !noSearch && bookId && !paraId) {
      toggleNav()
      initial.current = false
    }
  }, [mobile, toggleNav, noSearch, bookId, paraId])

  // CP-945: Breadcrumb bleibt leer
  // const arfbrBreadcrumbItem = {name: 'Arbeitsrecht für Betriebsräte', linkTo: arPathOverview()}
  // const breadcrumbs: Breadcrumb[] = [arfbrBreadcrumbItem]
  const breadcrumbs: Breadcrumb[] = []
  if (bookName && bookId) {
    // the book
    breadcrumbs.push({
      name: bookName,
      linkTo: arPathBook(query ?? '', bookId, undefined, undefined, filters, page),
      emphasize: true,
    })

    // the chapter
    if (documentInfo && documentInfo.documentId && documentInfo.documentName) {
      breadcrumbs.push({
        name: documentInfo.documentName,
        linkTo: arPathBook(query ?? '', bookId, documentInfo.documentId, undefined, filters, page),
      })
    }
  }

  const mobileBreadcrumbs: Breadcrumb[] = []
  if (!bookId || !query) {
    // CP-945: Breadcrumb bleibt leer
    // mobileBreadcrumbs.push(arfbrBreadcrumbItem)
  } else if (!navShown) {
    mobileBreadcrumbs.push({name: `Zurück zu den Suchergebnissen`, linkTo: arPathSearch(query, filters, page)})
  }

  const outerContainerStyle: IStyle = {
    position: 'relative',
    height: AR_SEARCH_AREA_HEIGHT,
  }
  const middleContainerStyle: IStyle = {
    position: 'absolute',
    height: AR_SEARCH_AREA_HEIGHT,
    paddingTop: 0,
    top: 0,
    left: 0,
    width: '100%',
    transitionProperty: 'height',
    transitionDuration: '0.1s',
  }
  const linkStyle: IStyle = {
    textDecoration: 'none',
    color: theme.colors.textInverted,
    '&:hover': {
      textDecoration: 'none',
      color: theme.colors.textInverted,
    },
    '&:visited': {
      textDecoration: 'none',
      color: theme.colors.textInverted,
    },
  }
  // The logo is taken out of the flow so that it cannot grow the button's line box.
  const kiaraButtonStyle: IStyle = {
    position: 'relative',
    border: 0,
    padding: 0,
    paddingLeft: KIARA_LOGO_SIZE + 4,
    marginRight: theme.spacing.space4,
    backgroundColor: 'transparent',
    color: theme.colors.textInverted,
    fontFamily: 'inherit',
    fontSize: theme.layout.reader.toolbarFontSize,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  }
  const kiaraLogoStyle: IStyle = {
    position: 'absolute',
    left: 0,
    top: '50%',
    marginTop: KIARA_LOGO_SIZE / -2,
    width: KIARA_LOGO_SIZE,
    height: KIARA_LOGO_SIZE,
  }
  // The bar is a single nowrap row. On narrow screens the breadcrumb is the part that gives way,
  // so that the KIARA toggle and "Alle Inhalte" stay fully readable and tappable.
  const mobileBreadcrumbsStyle: IStyle = {
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  }
  const barActionsStyle: IStyle = {
    flexShrink: 0,
  }
  return (
    <Box>
      <HeaderPanel logo={<DigitalLogo />} noSearch />
      {!noSearch && (
        <Flex fullWidth style={outerContainerStyle}>
          <Flex style={[clippedBackgroundImageRules(arSearchAreaBackgroundImage, true), middleContainerStyle]} />
          <Box style={[centeredLayoutRules(theme), containerRules(theme)]} fullWidth>
            <Box fullWidth py={4} maxWidth={theme.layout.search.maxWidth}>
              <ArSearchInput />
            </Box>
          </Box>
        </Flex>
      )}
      <Flex<Theme>
        bg={theme.colors.primaryLight}
        color={theme.colors.textInverted}
        fontStyle="brand"
        height={theme.layout.reader.toolbarHeight}
        alignItems="center"
      >
        <Box style={[centeredLayoutRules(theme), containerRules(theme)]} fullWidth>
          <Flex pl={2} nowrap justifyContent="space-between" alignItems="center">
            {!mobile && (
              <Box display={['none', 'block']}>
                <ReaderBreadcrumbsPanel breadcrumbs={breadcrumbs} />
              </Box>
            )}

            {mobile && (
              <Flex display={['block', 'none']} nowrap alignItems="center" style={mobileBreadcrumbsStyle}>
                {bookId && (
                  <Box onClick={() => toggleNav()} pr={2} style={{position: 'relative', left: -3}}>
                    <ThemedIcon
                      size={theme.layout.reader.toolbarIconSize}
                      color={theme.colors.iconInverted}
                      hoverColor={theme.colors.iconInverted}
                      verticalCenter
                    >
                      {navShown ? <CloseIcon /> : <TocIcon />}
                    </ThemedIcon>
                  </Box>
                )}
                <ReaderBreadcrumbsPanel breadcrumbs={mobileBreadcrumbs} />
              </Flex>
            )}

            <Flex nowrap alignItems="center" style={barActionsStyle}>
              <Box mr={8} style={{fontSize: theme.layout.reader.toolbarFontSize}}>
                <a
                  href={arPathContentResources()}
                  onClick={(e) => {
                    e.preventDefault()
                    closeKiara()
                    history.push(arPathContentResources())
                  }}
                  className={css(linkStyle)}
                >
                  Alle Inhalte
                </a>
              </Box>
              {kiaraEnabled && (
                <button type="button" aria-pressed={showKiara} onClick={toggleKiara} className={css(kiaraButtonStyle)}>
                  <img src={kiaraLogo} alt="" className={css(kiaraLogoStyle)} />
                  {showKiara ? 'KIARA schließen' : 'KIARA öffnen'}
                </button>
              )}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  )
}
